/**
 * Concept catalogue: >= 1,000 reproducible meme concepts (modifier x subject), separate from live tokens.
 * Each modifier maps to a visible art overlay so names and pictures agree ("King Duck" wears a crown).
 */
import { hashStr, Rng } from './rng';
import { SUBJECTS, type Category, type Subject } from './subjects';

export type Overlay = 'none' | 'crown' | 'tophat' | 'shades' | 'halo' | 'party' | 'zzz' | 'tears' | 'laser' | 'money' | 'stars' | 'sweat' | 'baby' | 'angry' | 'wizard' | 'chain' | 'mustache';
export type Style = 'sticker' | 'doodle' | 'collage' | 'crude' | 'photo';
export interface Concept { id: number; name: string; ticker: string; cat: Category; subject: string; overlay: Overlay; style: Style; bg: string; caption: boolean }

const MODS: [string, Overlay][] = [['', 'none'], ['King', 'crown'], ['Sir', 'tophat'], ['Cool', 'shades'], ['Holy', 'halo'], ['Party', 'party'], ['Sleepy', 'zzz'], ['Sad', 'tears'], ['Laser', 'laser'],
  ['Rich', 'money'], ['Space', 'stars'], ['Nervous', 'sweat'], ['Baby', 'baby'], ['Angry', 'angry'], ['Wizard', 'wizard'], ['Drip', 'chain'], ['Monsieur', 'mustache']];
const BGS = ['#7cc4ff', '#ff9ec7', '#ffe066', '#a6e3a1', '#c3a6ff', '#ffb37a', '#f6f1e7', '#ff6b6b', '#5ad1c8', '#e9e9e9', '#2b2d42', '#b8f2e6'];
const STYLES: Style[] = ['sticker', 'doodle', 'collage', 'crude'];

function tickerOf(mod: string, subj: Subject): string {
  const s = subj.name.toUpperCase().replace(/[^A-Z0-9]/g, '');
  if (!mod) return s.slice(0, 10);
  const m = mod.toUpperCase();
  return (m.length + s.length <= 10 ? m + s : (m.slice(0, 4) + s).slice(0, 10));
}

function build(): Concept[] {
  const out: Concept[] = []; const tickers = new Set<string>(); const names = new Set<string>();
  const r = new Rng(0xC0FFEE);
  for (const subj of SUBJECTS) {
    // bare subject + a seeded selection of modifiers per subject (stable)
    const mods = [MODS[0]!, ...MODS.slice(1).filter(() => r.next() < 0.3)];
    for (const [mod, overlay] of mods) {
      const name = (mod ? `${mod} ${subj.name}` : subj.name).slice(0, 32);
      let ticker = tickerOf(mod, subj); let k = 2;
      while (tickers.has(ticker)) ticker = ticker.slice(0, 9) + k++;
      if (names.has(name)) continue;
      names.add(name); tickers.add(ticker);
      const photo = subj.src.startsWith('p:');
      out.push({ id: out.length, name, ticker, cat: subj.cat, subject: subj.key, overlay, style: photo ? 'photo' : STYLES[hashStr(name) % STYLES.length]!, bg: BGS[hashStr(name + 'bg') % BGS.length]!, caption: hashStr(name + 'cap') % 5 === 0 });
    }
  }
  return out;
}
export const CONCEPTS: Concept[] = build();
export function conceptOf(id: number | undefined): Concept | undefined { return id == null ? undefined : CONCEPTS[id]; }
export function pickConcept(r: Rng): Concept { return CONCEPTS[Math.floor(r.next() * CONCEPTS.length)]!; }
